import { Link } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { cn } from "@/lib/cn";
import { Input } from "@/components/ui-kit/Input";
import { Pill } from "@/components/ui-kit/Pill";
import { useFunder } from "@/funder/FunderContext";

function LevelBadges({ levels }) {
  return (
    <span className="inline-flex gap-1" data-testid="matter-levels">
      {["l1", "l2", "l3"].map((k) => (
        <span
          key={k}
          className={cn(
            "rounded px-1.5 py-0.5 text-[11px] font-semibold uppercase",
            levels?.[k] ? "bg-status-paid/15 text-status-paid" : "bg-secondary text-muted-foreground/50"
          )}
        >
          {k}
        </span>
      ))}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="truncate text-sm font-medium text-foreground">{children}</div>
    </div>
  );
}

/** Compact one-row matter header for the workbench command center. */
export function MatterHeader({ matter, clientName, clientId, search, onSearch, onChanged }) {
  const { abbr, typeLabel } = useFunder();
  const isAcsa = matter.type === "ACSA_Client";
  const toggleReview = async () => {
    await api.patch(`/matters/${matter.id}`, { require_time_review: !matter.require_time_review });
    onChanged?.();
  };

  return (
    <div className="rounded-lg border border-border bg-surface shadow-card" data-testid="matter-header">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-3">
        <div className="flex items-center gap-3">
          <Link
            to={`/clients-matters/${clientId}`}
            data-testid="back-to-client"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to client
          </Link>
          <span className="font-serif text-lg font-semibold text-foreground">
            {clientName} · {matter.name}
          </span>
          <Pill tone={matter.status === "Open" ? "success" : "muted"}>{matter.status}</Pill>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="workbench-search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search this workbench…"
            className="h-9 pl-9"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-5 py-3 sm:grid-cols-3 lg:grid-cols-6">
        <Field label="Type">{typeLabel(matter.type)}</Field>
        <Field label="Rate">{money(matter.hourly_rate)}/hr</Field>
        <Field label="Increment">{matter.billing_increment_min} min</Field>
        <Field label={`${abbr} levels`}>{isAcsa ? <LevelBadges levels={matter.levels} /> : "—"}</Field>
        <Field label="Retainer (client)">{money(matter.retainerBalance || 0)}</Field>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Require review</span>
          <button
            type="button"
            role="switch"
            aria-checked={matter.require_time_review}
            data-testid="matter-require-review-toggle"
            onClick={toggleReview}
            className={cn(
              "relative h-6 w-11 shrink-0 rounded-full transition-colors",
              matter.require_time_review ? "bg-primary" : "bg-input"
            )}
          >
            <span
              className={cn(
                "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
                matter.require_time_review ? "translate-x-[22px]" : "translate-x-0.5"
              )}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
